import React, { useEffect, useState } from 'react';
import {
  StackLayout,
  GDSStyleObject,
  Card,
  Column,
  ColumnLayout,
  Box,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { getAccessToken } from '@leagueplatform/engen-capability-shared-library/src/utils/api';
import { DynamicModal } from '@leagueplatform/engen-capability-shared-library/src/components/layouts/dynamicModal';
import { glossary } from '../insurance-glossary.config';
import { PageHeader } from './pages/page-header.component';
import { ChatSectionComponent } from './pages/chat-section.component';
import { GlossaryAccordionComponent } from './pages/glossary-accordion.component';
import * as constants from '../common/constant';
import { GlossaryInfo } from '../types/glossary-data-type.types';
import GlossaryContent from '../assets/json/glossary-content.json';

export function GlossaryComponent() {
  const [glossaryApiFailure, setGlossaryApiFailure] = useState<boolean>(false);
  const [glossaryInfoLoading, setGlossaryInfoLoading] =
    useState<boolean>(false);

  const [glossaryInformation, setGlossaryInformation] =
    useState<GlossaryInfo[]>();
  const config = glossary.getConfig();
  const highmarkURL = config.HIGHMARK_API_URL;
  const glossaryInfoURL = `${highmarkURL}/digital/v3/members/benefits/glossary-terms`;

  const getGlossaryInformation = (url: string) => {
    setGlossaryInfoLoading(true);
    getAccessToken(url)
      .then((result: any) => {
        setGlossaryInfoLoading(false);
        if (result) {
          setGlossaryInformation(result.glossaryTerms);
          setGlossaryApiFailure(false);
        }
        if (result.errMsg) {
          setGlossaryApiFailure(true);
        }
      })
      .catch(() => {
        setGlossaryApiFailure(true);
        setGlossaryInfoLoading(false);
      });
  };

  useEffect(() => {
    getGlossaryInformation(glossaryInfoURL);
  }, [glossaryInfoURL]);

  const closeAPIFailureModal = () => {
    setGlossaryApiFailure(false);
  };

  return (
    <Box
      css={
        {
          backgroundColor: '$surfaceBackgroundPrimary',
          minHeight: '100vh',
        } as GDSStyleObject
      }
      className="glossaryTerms"
      data-testid="glossaryTerms"
    >
      <Box>
        <PageHeader showBackButton />
      </Box>
      <Box>
        <Card.Flat>
          <Card.Section
            css={
              {
                boxSizing: 'border-box',
                margin: '0px auto',
                width: '100%',
                maxWidth: '1215px',
                desktop: {
                  padding: '$none',
                },
                '@mobile': {
                  padding: '$none',
                },
              } as GDSStyleObject
            }
          >
            <StackLayout
              orientation="horizontal"
              spacing={{ '@desktop': '$two' }}
              css={{ display: 'block', padding: '$none' }}
            >
              <Box
                css={{
                  '@desktop': {
                    padding: '$twoAndHalf $oneAndHalf',
                  },
                  '@mobile': {
                    padding: '$oneAndHalf',
                  },
                }}
              >
                <UtilityText
                  css={
                    {
                      fontSize: '$bodyOne',
                      fontWeight: 400,
                      lineHeight: '$headingFour',
                      display: 'flex',
                      '@desktop': {
                        width: '575px',
                      },
                      '@mobile': {
                        marginLeft: '0px',
                        width: '355px',
                      },
                      '@tablet': {
                        marginLeft: '30px',
                      },
                      '@laptop': {
                        marginLeft: '30px',
                      },
                    } as GDSStyleObject
                  }
                >
                  {GlossaryContent.GLOSSARY.GLOSSARYSUBSECTIONTITLE}
                </UtilityText>
              </Box>
              {glossaryInfoLoading && (
                <Box
                  css={
                    {
                      display: 'flex',
                      marginTop: '0px',
                      flexDirection: 'row',
                      padding: '$half',
                      height: '80px',
                      '@mobile': { flexDirection: 'column' },
                      '@tablet': { flexDirection: 'column' },
                      '@mobileLandscape': { flexDirection: 'column' },
                    } as GDSStyleObject
                  }
                >
                  <LoadingIndicator data-testid="glossaryLoadingPanel" />
                </Box>
              )}
              <Box
                css={
                  {
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'row',
                    padding: '$none',

                    '@mobile': { flexDirection: 'column' },
                    '@tablet': { flexDirection: 'column' },
                    '@mobileLandscape': { flexDirection: 'column' },
                  } as GDSStyleObject
                }
              >
                <ColumnLayout
                  background="primary"
                  css={{
                    '@desktop': {
                      width: '1215px',
                      spacingStart: '12px',
                      paddingInlineStart: '$oneAndHalf',
                    },
                    '@mobile': {
                      paddingInlineStart: '0px',
                      paddingInlineEnd: '0px',
                    },
                    marginBottom: '32px',
                  }}
                >
                  <Column
                    width={{
                      '@initial': 8,
                      '@mobile': 4,
                      '@mobileLandscape': 5,
                      '@tablet': 5,
                    }}
                  >
                    <GlossaryAccordionComponent
                      glossaryInfoLoading={glossaryInfoLoading}
                      glossaryApiFailure={glossaryApiFailure}
                      glossaryInformation={glossaryInformation}
                    />
                  </Column>
                  <Column
                    width={{
                      '@initial': 4,
                      '@mobile': 4,
                      '@mobileLandscape': 5,
                      '@tablet': 3,
                    }}
                    css={{
                      '@desktop': {
                        padding: '$threeQuarters',
                      },
                      '@mobile': {
                        marginTop: '8px',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        marginBottom: '32px',
                      },
                      '@mobileLandscape': {
                        marginLeft: 'auto',
                        marginRight: 'auto',
                      },
                    }}
                  >
                    <ChatSectionComponent />
                  </Column>
                </ColumnLayout>
              </Box>
            </StackLayout>
          </Card.Section>
        </Card.Flat>
      </Box>
      {!glossaryInfoLoading && glossaryApiFailure && (
        <DynamicModal
          open={glossaryApiFailure}
          type="API_FAILED"
          heading={constants.API_FAILED_TITLE}
          description={constants.API_FAILED_CONTENT}
          description2={constants.API_FAILED_CONTENTSECOND}
          saveBtn={constants.OK}
          cancelBtn={constants.CANCEL_BTN}
          onClose={closeAPIFailureModal}
        />
      )}
    </Box>
  );
}
